/**
 * VegetationSystem - Prozedurale Vegetation und Straßenbegrenzungen
 */

import * as THREE from 'three';

interface TreeConfig {
  trunkRadius: number;
  trunkHeight: number;
  foliageRadius: number;
  foliageHeight: number;
  color: THREE.Color;
}

interface GuardrailConfig {
  height: number;
  segmentLength: number;
  color: THREE.Color;
}

const TREE_CONFIGS: { [key: string]: TreeConfig } = {
  pine: {
    trunkRadius: 0.3,
    trunkHeight: 8,
    foliageRadius: 3,
    foliageHeight: 6,
    color: new THREE.Color(0x2d5016)
  },
  oak: {
    trunkRadius: 0.4,
    trunkHeight: 6,
    foliageRadius: 4,
    foliageHeight: 5,
    color: new THREE.Color(0x3d6f1f)
  },
  birch: {
    trunkRadius: 0.25,
    trunkHeight: 7,
    foliageRadius: 2.5,
    foliageHeight: 5,
    color: new THREE.Color(0x4a7c59)
  },
  bush: {
    trunkRadius: 0.2,
    trunkHeight: 1.5,
    foliageRadius: 2,
    foliageHeight: 2,
    color: new THREE.Color(0x5a8c69)
  }
};

const GUARDRAIL_CONFIG: GuardrailConfig = {
  height: 1.2,
  segmentLength: 3,
  color: new THREE.Color(0x888888)
};

export class VegetationSystem {
  private group = new THREE.Group();
  private vegetationMap = new Map<string, THREE.Group>();

  constructor() {
    this.group.name = 'VegetationGroup';
  }

  /**
   * Erstellt einen einzelnen Baum
   */
  createTree(position: THREE.Vector3, treeType: keyof typeof TREE_CONFIGS = 'pine'): THREE.Group {
    const config = TREE_CONFIGS[treeType];
    const treeGroup = new THREE.Group();

    // Stammglü
    const trunkGeometry = new THREE.CylinderGeometry(config.trunkRadius, config.trunkRadius * 0.8, config.trunkHeight, 8);
    const trunkMaterial = new THREE.MeshStandardMaterial({ color: 0x664422, roughness: 0.8 });
    const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
    trunk.position.y = config.trunkHeight / 2;
    trunk.castShadow = true;
    trunk.receiveShadow = true;
    treeGroup.add(trunk);

    // Laubwerk (Kegel für einfache Silhouette)
    const foliageGeometry = new THREE.ConeGeometry(config.foliageRadius, config.foliageHeight, 8);
    const foliageMaterial = new THREE.MeshStandardMaterial({
      color: config.color,
      roughness: 0.7,
      metalness: 0.1
    });
    const foliage = new THREE.Mesh(foliageGeometry, foliageMaterial);
    foliage.position.y = config.trunkHeight + config.foliageHeight / 2;
    foliage.castShadow = true;
    foliage.receiveShadow = true;
    treeGroup.add(foliage);

    // Optionale zusätzliche Foliage-Schicht
    const foliageLayer2 = new THREE.Mesh(foliageGeometry.clone(), foliageMaterial.clone());
    foliageLayer2.position.y = config.trunkHeight + config.foliageHeight * 0.8;
    foliageLayer2.scale.set(0.7, 0.6, 0.7);
    foliageLayer2.castShadow = true;
    foliageLayer2.receiveShadow = true;
    treeGroup.add(foliageLayer2);

    treeGroup.position.copy(position);
    return treeGroup;
  }

  /**
   * Spawnt Vegetation in einem Bereich (zufällig auf Rasenflächee)
   */
  spawnVegetationArea(
    centerPosition: THREE.Vector3,
    radius: number,
    density: number = 10
  ): void {
    const treeTypes = Object.keys(TREE_CONFIGS) as Array<keyof typeof TREE_CONFIGS>;

    for (let i = 0; i < density; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * radius;
      const x = centerPosition.x + Math.cos(angle) * distance;
      const z = centerPosition.z + Math.sin(angle) * distance;

      const treeType = treeTypes[Math.floor(Math.random() * treeTypes.length)];
      const tree = this.createTree(new THREE.Vector3(x, 0, z), treeType);

      this.group.add(tree);
    }
  }

  /**
   * Erstellt eine Leitplanke/Straßenbegrenzung
   */
  createGuardrail(startPosition: THREE.Vector3, endPosition: THREE.Vector3): THREE.Group {
    const railGroup = new THREE.Group();

    const direction = endPosition.clone().sub(startPosition);
    const distance = direction.length();
    const segmentCount = Math.ceil(distance / GUARDRAIL_CONFIG.segmentLength);

    for (let i = 0; i < segmentCount; i++) {
      const t = i / segmentCount;
      const position = startPosition.clone().lerp(endPosition, t);

      // Pfosten
      const postGeometry = new THREE.BoxGeometry(0.1, GUARDRAIL_CONFIG.height, 0.1);
      const postMaterial = new THREE.MeshStandardMaterial({
        color: new THREE.Color(0x555555),
        roughness: 0.6,
        metalness: 0.4
      });
      const post = new THREE.Mesh(postGeometry, postMaterial);
      post.position.copy(position);
      post.position.y = GUARDRAIL_CONFIG.height / 2;
      post.castShadow = true;
      railGroup.add(post);

      // Querstange
      const railGeometry = new THREE.BoxGeometry(GUARDRAIL_CONFIG.segmentLength, 0.05, 0.05);
      const railMaterial = new THREE.MeshStandardMaterial({
        color: GUARDRAIL_CONFIG.color,
        roughness: 0.5,
        metalness: 0.6
      });
      const rail = new THREE.Mesh(railGeometry, railMaterial);
      rail.position.copy(position);
      rail.position.y = GUARDRAIL_CONFIG.height * 0.6;
      rail.lookAt(endPosition);
      rail.castShadow = true;
      railGroup.add(rail);
    }

    return railGroup;
  }

  /**
   * Spawnt Guardrails entlang einer Straße
   */
  spawnGuardrailsAlongRoad(roadPositions: THREE.Vector3[]): void {
    if (roadPositions.length < 2) return;

    for (let i = 0; i < roadPositions.length - 1; i++) {
      const railgroup = this.createGuardrail(roadPositions[i], roadPositions[i + 1]);
      this.group.add(railgroup);
    }
  }

  /**
   * Erstellt ein Verkehrsschild
   */
  createTrafficSign(
    position: THREE.Vector3,
    signType: 'stop' | 'speed' | 'yield' | 'parking',
    text?: string
  ): THREE.Group {
    const signGroup = new THREE.Group();

    // Pfosten (höher)
    const postGeometry = new THREE.CylinderGeometry(0.08, 0.08, 3, 8);
    const postMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });
    const post = new THREE.Mesh(postGeometry, postMaterial);
    post.position.y = 1.5;
    post.castShadow = true;
    signGroup.add(post);

    // Schild basierend auf Typ (3x größer)
    let signGeometry: THREE.BufferGeometry;
    let signColor: THREE.Color;

    switch (signType) {
      case 'stop':
        signGeometry = new THREE.CylinderGeometry(1.2, 1.2, 0.15, 8);
        signColor = new THREE.Color(0xff0000);
        break;
      case 'speed':
        signGeometry = new THREE.BoxGeometry(1.8, 2.4, 0.15);
        signColor = new THREE.Color(0xffff00);
        break;
      case 'yield':
        signGeometry = new THREE.CylinderGeometry(0.9, 0.9, 0.15, 3);
        signColor = new THREE.Color(0xff6600);
        break;
      case 'parking':
        signGeometry = new THREE.BoxGeometry(1.5, 1.5, 0.15);
        signColor = new THREE.Color(0x0066ff);
        break;
    }

    const signMaterial = new THREE.MeshStandardMaterial({ color: signColor, roughness: 0.3, emissive: signColor, emissiveIntensity: 0.2 });
    const sign = new THREE.Mesh(signGeometry, signMaterial);
    sign.position.y = 3.2;
    sign.castShadow = true;
    signGroup.add(sign);

    signGroup.position.copy(position);
    return signGroup;
  }

  /**
   * Spawnt Verkehrsschilder an definierten Positionen
   */
  spawnTrafficSign(position: THREE.Vector3, signType: 'stop' | 'speed' | 'yield' | 'parking'): void {
    const sign = this.createTrafficSign(position, signType);
    this.group.add(sign);
  }

  /**
   * Spawnt mehrere Verkehrsschilder
   */
  spawnTrafficSigns(signPositions: Array<{ pos: THREE.Vector3; type: 'stop' | 'speed' | 'yield' | 'parking' }>): void {
    signPositions.forEach(({ pos, type }) => {
      this.spawnTrafficSign(pos, type);
    });
  }

  /**
   * Erstellt ein Ortsschild (für Dorf/Stadt Einfahrten)
   */
  createCitySign(cityName: string, position: THREE.Vector3, isEntering: boolean = true): THREE.Group {
    const signGroup = new THREE.Group();

    // Pfosten
    const postGeo = new THREE.CylinderGeometry(0.04, 0.05, 2.5, 8);
    const postMat = new THREE.MeshStandardMaterial({ color: 0x333333, roughness: 0.6 });
    const post = new THREE.Mesh(postGeo, postMat);
    post.position.y = 1.25;
    post.castShadow = true;
    signGroup.add(post);

    // Schild-Platte
    const plateGeo = new THREE.BoxGeometry(1.8, 1.2, 0.08);
    const color = isEntering ? 0x008800 : 0xcccccc; // Grün für Entering, Grau für Leaving
    const plateMat = new THREE.MeshStandardMaterial({ color, roughness: 0.5, metalness: 0.1 });
    const plate = new THREE.Mesh(plateGeo, plateMat);
    plate.position.y = 2.1;
    plate.position.z = 0.05;
    plate.castShadow = true;
    signGroup.add(plate);

    signGroup.position.copy(position);
    return signGroup;
  }

  /**
   * Spawnt Ortsschilder beim Betreten von Städten
   */
  spawnCitySigns(cityName: string, position: THREE.Vector3): void {
    const enterSign = this.createCitySign(cityName, position.clone(), true);
    this.group.add(enterSign);
  }

  /**
   * Erstellt Ampel an einer Kreuzung
   */
  createTrafficLight(position: THREE.Vector3): THREE.Group {
    const lightGroup = new THREE.Group();

    // Pfosten
    const postGeometry = new THREE.CylinderGeometry(0.08, 0.08, 4, 8);
    const postMaterial = new THREE.MeshStandardMaterial({ color: 0x000000 });
    const post = new THREE.Mesh(postGeometry, postMaterial);
    post.position.y = 2;
    post.castShadow = true;
    lightGroup.add(post);

    // Ampelkasten
    const boxGeometry = new THREE.BoxGeometry(0.4, 1, 0.15);
    const boxMaterial = new THREE.MeshStandardMaterial({ color: 0x222222 });
    const box = new THREE.Mesh(boxGeometry, boxMaterial);
    box.position.y = 3.5;
    box.castShadow = true;
    lightGroup.add(box);

    // Lichter (rot, gelb, grün)
    const lightColors = [0xff0000, 0xffff00, 0x00ff00]; // Rot, Gelb, Grün
    const lightPositions = [0.7, 0, -0.7];

    lightColors.forEach((color, index) => {
      const lightGeometry = new THREE.SphereGeometry(0.12, 8, 8);
      const lightMaterial = new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.5 });
      const light = new THREE.Mesh(lightGeometry, lightMaterial);
      light.position.y = 3.5 + lightPositions[index];
      light.position.z = 0.05;
      lightGroup.add(light);
    });

    lightGroup.position.copy(position);
    return lightGroup;
  }

  /**
   * Spawnt Ampel(n) an Kreuzungen
   */
  spawnTrafficLights(intersections: THREE.Vector3[]): void {
    intersections.forEach(pos => {
      const light = this.createTrafficLight(pos);
      this.group.add(light);
    });
  }

  /**
   * Gibt die Gruppe zuRück
   */
  getGroup(): THREE.Group {
    return this.group;
  }

  /**
   * Löscht alle Vegetation
   */
  clear(): void {
    this.group.clear();
    this.vegetationMap.clear();
  }

  /**
   * Aktualisiert Vegetation basierend auf Spielerposition (LOD-System)
   */
  updateVisibility(playerPosition: THREE.Vector3, viewDistance: number = 500): void {
    this.group.children.forEach(child => {
      const distance = playerPosition.distanceTo(child.position);
      child.visible = distance < viewDistance;
    });
  }
}

export default VegetationSystem;
